import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import { useDeliveryDayMarketplaces, useMarketplaces, useDeliveryDays } from './apiHooks';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';
import utils from '../../utils/utils';
import Select from 'react-select';
import DATE_FORMAT from '../../components/constants/dateFormat';
import MultiSelect from '../../components/customFields/multiSelect';

const AdminCombinedOrderItems = () => {
  const [marketplaceId, setMarketplaceId] = useState(null);
  const [selectedDeliveryDayIds, setSelectedDeliveryDayIds] = useState([]);

  const {
    marketplaces,
    isLoading: marketplacesLoading,
    isError: isMarketplacesError,
    error: marketplacesError,
  } = useMarketplaces();

  const {
    deliveryDayMarketplaces,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useDeliveryDayMarketplaces({ marketplaceId, deliveryDayIds: selectedDeliveryDayIds?.join(',') });

  const { isLoading: loadingDeliveryDays, deliveryDays } = useDeliveryDays({
    showAll: true,
    sortBy: 'deliveryDateDesc',
  });

  useEffect(() => {
    if (marketplaceId && selectedDeliveryDayIds.length) {
      refetch();
    }
  }, [marketplaceId, selectedDeliveryDayIds]);

  const { formatMessage } = useIntl();

  const combinedCombinedOrderItems = deliveryDayMarketplaces
    ? deliveryDayMarketplaces.reduce((result, ddm) => {
        if (ddm.combinedOrderItems) {
          return ddm.combinedOrderItems.reduce((coiResult, combinedOrderItem) => {
            const newCoiResult = [...coiResult];
            const matchinCoiIndex = coiResult.findIndex((coi) => coi.itemId === combinedOrderItem.item.id);

            const hasFulfilledQuantity = combinedOrderItem.fulfilledQuantity != null;
            const totalQuantityAddition = hasFulfilledQuantity
              ? combinedOrderItem.fulfilledQuantity
              : combinedOrderItem.orderedQuantity;

            if (matchinCoiIndex >= 0) {
              newCoiResult[matchinCoiIndex].totalFulfilledQuantity += totalQuantityAddition;
              newCoiResult[matchinCoiIndex].totalOrderedQuantity += combinedOrderItem.orderedQuantity;
              newCoiResult[matchinCoiIndex].deliveryDateQuantities.push({
                deliveryDate: ddm.deliveryDate,
                fulfilledQuantity: combinedOrderItem.fulfilledQuantity,
                orderedQuantity: combinedOrderItem.orderedQuantity,
                hasFulfilledQuantity,
              });
            } else {
              newCoiResult.push({
                itemId: combinedOrderItem.item.id,
                itemName: utils.getItemName(combinedOrderItem.item, formatMessage),
                wholesalePrice: combinedOrderItem.item.priceFields.wholesalePrice,
                totalFulfilledQuantity: totalQuantityAddition,
                totalOrderedQuantity: combinedOrderItem.orderedQuantity,
                deliveryDateQuantities: [
                  {
                    deliveryDate: ddm.deliveryDate,
                    fulfilledQuantity: combinedOrderItem.fulfilledQuantity,
                    orderedQuantity: combinedOrderItem.orderedQuantity,
                    hasFulfilledQuantity,
                  },
                ],
              });
            }

            return newCoiResult;
          }, result);
        } else {
          return result;
        }
      }, [])
    : null;

  const totalSuggestedSum = combinedCombinedOrderItems?.reduce((result, ccoi) => {
    if (ccoi.wholesalePrice) {
      return result + ccoi.wholesalePrice * ccoi.totalFulfilledQuantity;
    } else {
      return result;
    }
  }, 0);

  return (
    <div>
      <Helmet>
        <title>{formatMessage({ id: 'admin.combined.order.items.title' })}</title>
      </Helmet>
      <h3>
        <FormattedMessage id={'admin.combined.order.items.title'} />
      </h3>

      <div className="m-b-3">
        <Link className="btn btn-default" to="/admin/items/create">
          <FormattedMessage id={'admin.button.create'} />
        </Link>
      </div>

      <div className="m-b-3 row">
        {marketplaces ? (
          <div className="col-12 col-sm-6">
            <div className="form-group m-b-1">
              <label htmlFor="marketplaceSelect">
                <FormattedMessage id={'admin.combined.order.items.marketplace.select.label'} />
              </label>
              <Select
                name="marketplaceSelect"
                value={
                  marketplaceId && marketplaces
                    ? {
                        value: marketplaceId,
                        label: marketplaces.find((mp) => mp.id === marketplaceId)?.name || '-',
                      }
                    : null
                }
                onChange={(mp) => setMarketplaceId(mp.value)}
                options={marketplaces.map((marketplace) => ({
                  value: marketplace.id,
                  label: `${marketplace.name}`,
                }))}
              />
            </div>
          </div>
        ) : (
          ''
        )}
        {deliveryDays ? (
          <div className="col-12 col-sm-6">
            <div className="form-group">
              <MultiSelect
                schema={{
                  title: formatMessage({
                    id: 'admin.combined.order.items.delivery.days.select.label',
                  }),
                  options: deliveryDays.map((deliveryDay) => ({
                    value: deliveryDay.id,
                    label: `${utils.getFormattedDateFromString(
                      deliveryDay.deliveryDate,
                      DATE_FORMAT.displayWeekdayAndDate
                    )}`,
                  })),
                }}
                onChange={(value) => {
                  if (value) {
                    setSelectedDeliveryDayIds(value);
                  } else {
                    setSelectedDeliveryDayIds([]);
                  }
                }}
                formData={selectedDeliveryDayIds}
              />
            </div>
          </div>
        ) : (
          ''
        )}
      </div>

      <Loader show={loading || marketplacesLoading || loadingDeliveryDays} />
      {isError || isMarketplacesError ? (
        <ErrorMessage error={utils.getErrorMessageFromResponse(error || marketplacesError)} />
      ) : (
        ''
      )}

      {totalSuggestedSum ? (
        <div className="m-b-3 text-large">
          <FormattedMessage
            id={'admin.combined.order.items.total.suggested.sum'}
            values={{ sum: <strong className="text-h3">{utils.getRoundedPrice(totalSuggestedSum)}€</strong> }}
          />
        </div>
      ) : (
        ''
      )}

      <table className="table table-striped admin__table m-bottom-margin">
        <thead>
          <tr>
            <th scope="col">
              <FormattedMessage id={'admin.combined.order.items.table.name'} />
            </th>
            <th scope="col">
              <FormattedMessage id={'admin.combined.order.items.table.wholesale.price'} />
            </th>
            <th scope="col">
              <FormattedMessage id={'admin.combined.order.items.table.fulfilled.quantity'} />
            </th>
          </tr>
        </thead>
        <tbody>
          {combinedCombinedOrderItems?.map((ccoi, index) => (
            <tr key={index}>
              <td scope="row">
                <div> {ccoi.itemName}</div>
                <div>
                  <small>#{ccoi.itemId}</small>
                </div>
              </td>
              <td>
                <div>{ccoi.wholesalePrice}€</div>
              </td>
              <td>
                <div
                  className={`text-semi-bold ${
                    ccoi.totalFulfilledQuantity !== ccoi.totalOrderedQuantity ? 'text-danger' : ''
                  }`}
                >
                  {ccoi.totalFulfilledQuantity}
                </div>
                <div>
                  <small>
                    <FormattedMessage
                      id={'admin.combined.order.items.table.total.ordered.quantity'}
                      values={{ quantity: ccoi.totalOrderedQuantity }}
                    />
                  </small>
                </div>
                {ccoi.deliveryDateQuantities.map((ddq) => (
                  <div key={ddq.deliveryDate}>
                    <small className="text-muted">
                      {utils.getFormattedDateFromString(ddq.deliveryDate, DATE_FORMAT.displayWeekdayAndDate)}:{' '}
                      <span className={`${ddq.fulfilledQuantity !== ddq.orderedQuantity ? 'text-danger' : ''}`}>
                        {ddq.hasFulfilledQuantity ? ddq.fulfilledQuantity : ddq.orderedQuantity}{' '}
                        {ddq.hasFulfilledQuantity ? (
                          ''
                        ) : (
                          <FormattedMessage id={'admin.combined.order.items.table.fulfilled.quantity.not.present'} />
                        )}
                      </span>{' '}
                      {ddq.hasFulfilledQuantity ? (
                        <FormattedMessage
                          id={'admin.combined.order.items.table.ordered.quantity'}
                          values={{ quantity: ddq.orderedQuantity }}
                        />
                      ) : (
                        ''
                      )}
                    </small>
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminCombinedOrderItems;
